import React, { forwardRef, useContext } from 'react';
import { useController } from 'react-hook-form';
import { useMergeRefs } from '../../hooks/useMergeRefs';
import { SelectProps } from '../Select';
import { SelectField } from '../Select/SelectField';
import { FormContext } from './Form';
import { BoundComponentProps } from './types';

export type BoundSelectProps = SelectProps & BoundComponentProps;

export const BoundSelect = forwardRef<HTMLButtonElement, BoundSelectProps>(
  (
    {
      id,
      control,
      type,
      name,
      defaultValue,
      rules,
      label,
      required,
      readOnly,
      options,
      disabled,
      onChange: outerOnChange,
      ...rest
    },
    ref,
  ) => {
    const { control: contextControl } = useContext(FormContext);

    const {
      field: { ref: internalRef, value, onChange, ...selectProps },
      fieldState: { error },
    } = useController({
      name,
      control: control ?? contextControl,
      defaultValue,
      rules: {
        required: required && 'This field is required',
        ...rules,
      },
    });

    const combinedRef = useMergeRefs(ref, internalRef);

    return (
      <SelectField
        ref={combinedRef}
        id={id}
        label={label}
        value={value}
        options={options}
        type={type}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        error={error?.message}
        onChange={(newValue: string) => {
          onChange(newValue);
          outerOnChange?.(newValue);
        }}
        {...selectProps}
        {...rest}
      />
    );
  },
);

BoundSelect.displayName = 'BoundSelect';
